/**
 * @file 残した記録をあとから直す枠 (記録・おんがく・よむの一覧の行に開く)。
 *
 * @what: 一覧の行の「なおす」で開き、日付・教材 (教本)・ページ・分・様子・写真を直して保存する。
 *   写真は付いていなければ足せて、付いていれば変える・外すができる。面ごとに出す項目が違う
 *   ので kind で切り替える (学習 = ページ、おんがく = 分、よむ = また読みたい)。
 * @why: 残したあとに写真を足したい、日付を間違えた、といった直しが今までできなかった
 *   (代表 2026-08-28)。保存は同じ id への送り直しで、サーバが上書きとして受ける。画面に
 *   出していない項目は logToInput が写し取るので、直した拍子に消えない。
 * @failure: 写真のアップロードに失敗したら記録は送らず、枠の中に文言を出す (写真なしで
 *   保存し直せる)。保存そのものの失敗は面ごとの actionError が 1 行で知らせる。
 *   教材のページを小さく直しても、教材の「いまのページ」は戻らない (サーバは前進だけ扱う)。
 */

import { useMemo, useRef, useState } from "react";
import { todayLocal } from "../api/dates";
import type { StudyProgressLog, StudyProgressLogInput } from "../api/plans";
import { applyPhotoEdit, logToInput } from "../lib/logEdit";
import { deleteStudyLogImage } from "../lib/studyLogImage";
import { parseReadingLog, serializeReadingLog } from "../model/readingLog";
import { LogImageThumb } from "./StudyLogImage";
import "./study.css";

/** 面ごとの出し分け。学習 = ページ、おんがく = 分、よむ = また読みたい。 */
export type LogEditKind = "study" | "music" | "reading";

/** 教材 (教本) の選択肢。 */
export interface LogEditMaterial {
  id: string;
  title: string;
}

export function LogEditForm({
  log,
  familyId,
  kind,
  materials,
  busy,
  onSave,
  onClose,
}: {
  log: StudyProgressLog;
  /** 添付画像のアップロード先 (families/{familyId}/study_log_images/)。 */
  familyId: string;
  kind: LogEditKind;
  /** 教材 (教本) の選択肢。空なら教材の欄を出さない。 */
  materials: readonly LogEditMaterial[];
  busy: boolean;
  onSave: (input: StudyProgressLogInput) => Promise<StudyProgressLog | null>;
  onClose: () => void;
}) {
  const reading = kind === "reading" ? parseReadingLog(log) : null;
  const [date, setDate] = useState(log.date);
  const [materialId, setMaterialId] = useState(log.material_id ?? "");
  const [fromPage, setFromPage] = useState(log.from_page ? String(log.from_page) : "");
  const [toPage, setToPage] = useState(log.to_page ? String(log.to_page) : "");
  const [minutes, setMinutes] = useState(log.minutes ?? 0);
  const [memo, setMemo] = useState(log.memo ?? "");
  const [again, setAgain] = useState(reading?.again === true);
  const [photo, setPhoto] = useState<File | null>(null);
  const [dropPhoto, setDropPhoto] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const materialLabel = kind === "music" ? "教本" : "教材";
  const memoLabel = kind === "music" ? "メモ" : kind === "reading" ? "ひとこと" : "様子";
  const withPhoto = kind !== "reading";
  // 一覧に無い教材 (使い終えて除外した本など) に紐づいた記録でも、選択が空に落ちないようにする。
  // 様子を 1 文字打つたびに教材を走査しないよう、教材と選択が変わったときだけ作り直す
  const options: LogEditMaterial[] = useMemo(
    () =>
      materialId && !materials.some((m) => m.id === materialId)
        ? [{ id: materialId, title: "（一覧に無い教材）" }, ...materials]
        : [...materials],
    [materials, materialId],
  );
  const keepsPhoto = Boolean(log.image_path) && !dropPhoto && !photo;

  const canSave =
    !saving &&
    !busy &&
    /^\d{4}-\d{2}-\d{2}$/.test(date) &&
    // from を入れたら to も要る (サーバの検証と同じ)
    !(kind === "study" && fromPage.trim() !== "" && toPage.trim() === "") &&
    !(kind === "music" && minutes <= 0);

  const clearPickedPhoto = () => {
    setPhoto(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setPhotoError(null);
    try {
      const draft = logToInput(log);
      draft.date = date;
      if (kind !== "reading") {
        if (materialId) draft.material_id = materialId;
        else delete draft.material_id;
      }
      if (kind === "study") {
        const from = Number(fromPage);
        const to = Number(toPage);
        if (fromPage.trim() !== "" && Number.isFinite(from) && from > 0) draft.from_page = from;
        else delete draft.from_page;
        if (toPage.trim() !== "" && Number.isFinite(to) && to > 0) draft.to_page = to;
        else delete draft.to_page;
      }
      if (kind === "music") draft.minutes = minutes;
      if (memo.trim()) draft.memo = memo.trim();
      else delete draft.memo;
      if (reading) draft.detail = serializeReadingLog(reading.book, again);

      let next = draft;
      let stale = "";
      if (withPhoto) {
        const applied = await applyPhotoEdit(draft, familyId, { photo, removed: dropPhoto });
        next = applied.input;
        stale = applied.stale;
      }
      const saved = await onSave(next);
      if (!saved) return;
      // 差し替え・取り外しで参照が切れた画像は片づける (best-effort)
      if (stale) deleteStudyLogImage(stale);
      onClose();
    } catch (err) {
      console.error("[study] failed to save an edited log", err);
      setPhotoError("写真を送れませんでした。写真を外して保存するか、時間を置いてお試しください。");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="study-log-edit study-log-form" role="group" aria-label="記録をなおす">
      <div className="study-log-form-row">
        <label className="study-log-field">
          <span>日付</span>
          {/* 学習の記録だけ先の日付を止めない (記録のフォームと同じ)。※なぜなら教材を
              いつ進めるかの予定を先の日付で残す使い方があり、直すときだけ入れられなくなると、
              入れてある記録を直せなくなるためである */}
          <input
            type="date"
            value={date}
            {...(kind === "study" ? {} : { max: todayLocal() })}
            onChange={(e) => setDate(e.target.value || log.date)}
          />
        </label>
        {kind === "music" ? (
          <label className="study-log-field">
            <span>分</span>
            <input
              type="number"
              min={1}
              max={600}
              value={minutes}
              onChange={(e) =>
                setMinutes(Math.min(600, Math.max(0, Math.floor(Number(e.target.value) || 0))))
              }
            />
          </label>
        ) : null}
        {kind !== "reading" && options.length > 0 ? (
          <label className="study-log-field">
            <span>{materialLabel}</span>
            <select value={materialId} onChange={(e) => setMaterialId(e.target.value)}>
              <option value="">（{materialLabel}によらない記録）</option>
              {options.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.title}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {kind === "study" ? (
          <label className="study-log-field study-log-field--page">
            <span>ページ</span>
            <span className="study-log-pages">
              <input
                type="number"
                min={1}
                max={9999}
                aria-label="始めたページ"
                value={fromPage}
                onChange={(e) => setFromPage(e.target.value)}
              />
              <span className="study-muted">〜</span>
              <input
                type="number"
                min={1}
                max={9999}
                aria-label="進んだページ"
                value={toPage}
                onChange={(e) => setToPage(e.target.value)}
              />
            </span>
          </label>
        ) : null}
      </div>

      {reading ? (
        <label className="study-reading-again">
          <input type="checkbox" checked={again} onChange={(e) => setAgain(e.target.checked)} />
          <span>また読みたがった</span>
        </label>
      ) : null}

      <label className="study-log-field">
        <span>{memoLabel}</span>
        <input type="text" maxLength={500} value={memo} onChange={(e) => setMemo(e.target.value)} />
      </label>

      <div className="study-log-form-actions">
        {withPhoto ? (
          <>
            {keepsPhoto && log.image_path ? (
              <>
                <LogImageThumb path={log.image_path} enc={log.image_enc} />
                <button type="button" className="study-link-btn" onClick={() => setDropPhoto(true)}>
                  写真を外す
                </button>
              </>
            ) : null}
            {dropPhoto && log.image_path && !photo ? (
              <span className="study-log-photo-dropped">
                保存すると写真が外れます
                <button
                  type="button"
                  className="study-link-btn"
                  onClick={() => setDropPhoto(false)}
                >
                  戻す
                </button>
              </span>
            ) : null}
            <label className="study-log-photo-field">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => {
                  setPhoto(e.target.files?.[0] ?? null);
                  setDropPhoto(false);
                }}
              />
              <span className="study-ghost-btn" role="button">
                {log.image_path && !dropPhoto ? "写真を変える" : "写真を足す"}
              </span>
            </label>
            {photo ? (
              <span className="study-log-photo-picked">
                {photo.name}
                <button type="button" className="study-link-btn" onClick={clearPickedPhoto}>
                  外す
                </button>
              </span>
            ) : null}
          </>
        ) : null}
        <button
          type="button"
          className="study-save-btn"
          disabled={!canSave}
          onClick={() => void save()}
        >
          保存する
        </button>
        <button type="button" className="study-ghost-btn" onClick={onClose}>
          やめる
        </button>
        {photoError ? (
          <span role="alert" className="study-save-error">
            {photoError}
          </span>
        ) : null}
      </div>
    </div>
  );
}
